/**
 * The upgrade prompt.
 *
 * Any request answered HTTP 402 (the user may not apply: no plan, a lapsed
 * plan, the month's allowance used, or a feature outside the plan) lands
 * here through `reportLicenseProblem`, called once by the HTTP client. The
 * Apply buttons also open it themselves, before calling the API, when the
 * cached subscription already says no (`useLicense().requireApply`). The
 * dialog (features/billing/UpgradeDialog, mounted in the dashboard shell)
 * renders whatever prompt is open.
 *
 * Kept free of imports from the HTTP client so the client can import it.
 */

import { create } from 'zustand'

import type { LicenseErrorCode, PlanFeatures, PlanKey, ProblemDetail } from '@/types/api'
import { toast } from './toast'

export interface LicensePrompt {
  reason: LicenseErrorCode
  /** The plan the user is on, when there is one. */
  plan?: PlanKey | null
  /** The missing feature, for `feature_not_in_plan`. */
  feature?: keyof PlanFeatures | null
  remaining?: number | null
  /** When this month's allowance resets, for `quota_exhausted`. */
  windowEnd?: string | null
  /** The allowance itself, when known (from the cached usage). */
  limit?: number | null
  /** The server's own sentence, shown when we have nothing better. */
  detail?: string | null
}

interface LicenseState {
  prompt: LicensePrompt | null
  /** Bumped on every 402, so the shell can refresh the cached subscription. */
  seen: number
  open: (prompt: LicensePrompt) => void
  close: () => void
}

export const useLicenseStore = create<LicenseState>((set) => ({
  prompt: null,
  seen: 0,
  open: (prompt) => set((state) => ({ prompt, seen: state.seen + 1 })),
  close: () => set({ prompt: null }),
}))

const CODES: LicenseErrorCode[] = [
  'subscription_required',
  'subscription_inactive',
  'quota_exhausted',
  'feature_not_in_plan',
]

const asCode = (value: unknown): LicenseErrorCode | null =>
  typeof value === 'string' && (CODES as string[]).includes(value) ? (value as LicenseErrorCode) : null

/** True for an error the upgrade prompt already handled (any 402); callers skip their own error toast. */
export function isLicenseError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 402
}

/** One short sentence for a toast. */
export function licenseHeadline(prompt: LicensePrompt): string {
  switch (prompt.reason) {
    case 'quota_exhausted':
      return "You've used this month's applications"
    case 'feature_not_in_plan':
      return prompt.feature === 'autoApply' || prompt.feature === 'scheduling'
        ? 'Auto Apply is part of Power'
        : 'Not part of your plan'
    case 'subscription_inactive':
      return 'Your subscription is not active'
    default:
      return 'Choose a plan to start applying'
  }
}

/** Called by the HTTP client for every 402 response: toast, then the dialog. */
export function reportLicenseProblem(problem: ProblemDetail | null, status: number): void {
  if (status !== 402) return
  const extra = (problem ?? {}) as Record<string, unknown>
  const reason = asCode(extra.reason) ?? asCode(problem?.code) ?? 'subscription_required'
  const prompt: LicensePrompt = {
    reason,
    plan: (extra.plan as PlanKey | null | undefined) ?? null,
    feature: (extra.feature as keyof PlanFeatures | undefined) ?? null,
    remaining: typeof extra.remaining === 'number' ? extra.remaining : null,
    windowEnd: typeof extra.windowEnd === 'string' ? extra.windowEnd : null,
    detail: problem?.detail || problem?.title || null,
  }
  // A bulk action can bounce several requests at once: one toast for the burst.
  const current = useLicenseStore.getState().prompt
  if (!current || current.reason !== prompt.reason) {
    toast.info(licenseHeadline(prompt), prompt.detail ?? undefined)
  }
  useLicenseStore.getState().open(prompt)
}
